import React, { useState } from 'react';
import { Volume2, Pencil, TrendingUp, ChevronRight, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const OnboardingStep = ({ icon: Icon, title, description, color, isActive }) => (
  <div className={`flex flex-col items-center text-center p-6 rounded-2xl transition-all duration-300 ${
    isActive ? `bg-${color}-50 border-2 border-${color}-200 scale-105` : 'bg-white/50'
  }`}>
    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
      isActive ? `bg-${color}-500` : 'bg-slate-100'
    }`}>
      <Icon className={`w-8 h-8 ${isActive ? 'text-white' : 'text-slate-400'}`} />
    </div>
    <h3 className={`font-heading text-lg mb-2 ${isActive ? `text-${color}-700` : 'text-slate-600'}`}>
      {title}
    </h3>
    <p className={`text-sm ${isActive ? `text-${color}-600` : 'text-slate-500'}`}>
      {description}
    </p>
  </div>
);

const Onboarding = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      icon: Volume2,
      title: "Écouter",
      description: "Familiarise-toi avec les sons thaïs en écoutant les consonnes, voyelles et mots.",
      color: "violet",
      tip: "Clique sur l'icône 🔊 pour écouter la prononciation native."
    },
    {
      icon: Pencil,
      title: "Pratiquer",
      description: "Entraîne-toi avec la calligraphie, le clavier ou les exercices de reconnaissance.",
      color: "fuchsia",
      tip: "L'Entraînement Intensif te propose des exercices adaptés à ton niveau."
    },
    {
      icon: TrendingUp,
      title: "Progresser",
      description: "Suis ta progression et reviens chaque jour pour ancrer tes apprentissages.",
      color: "emerald",
      tip: "La révision espacée (SRS) t'aide à mémoriser sur le long terme."
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden animate-fade-in-up max-h-[90dvh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-6 text-white relative flex-shrink-0">
          <button 
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Passer l'introduction"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-8 h-8" />
            <h2 className="font-heading text-2xl">Bienvenue sur ThaiPractice !</h2>
          </div>
          <p className="text-violet-100">
            Découvre comment apprendre le thaï efficacement en 3 étapes simples.
          </p>
        </div>

        {/* Scrollable content zone */}
        <div className="p-6 flex-1 overflow-y-auto min-h-0">
          <div className="grid grid-cols-3 gap-4 mb-6">
            {steps.map((step, index) => (
              <OnboardingStep
                key={index}
                icon={step.icon}
                title={step.title}
                description={step.description}
                color={step.color}
                isActive={index === currentStep}
              />
            ))}
          </div>

          {/* Current Step Tip */}
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-4 mb-6 border border-violet-100">
            <p className="text-sm text-violet-700">
              <span className="font-semibold">💡 Conseil :</span> {steps[currentStep].tip}
            </p>
          </div>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentStep 
                    ? 'bg-violet-500 w-8' 
                    : index < currentStep 
                      ? 'bg-violet-300' 
                      : 'bg-slate-200'
                }`}
                aria-label={`Étape ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Actions - fixed footer */}
        <div className="flex justify-between items-center p-6 pt-4 flex-shrink-0">
          <button 
            onClick={handleSkip}
            className="text-slate-500 hover:text-slate-700 text-sm font-medium"
          >
            Passer l'introduction
          </button>
          <Button
            onClick={handleNext}
            className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white rounded-xl px-6"
          >
            {currentStep < steps.length - 1 ? (
              <>
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                Commencer
                <Sparkles className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
